/**
 * VIB-2094: «Tilbud i pipe» per rådgiver — DL-widgetens regnestykke.
 *
 * Ren kalkyle, ingen React eller Supabase, samme stil som
 * offer-pipeline-calc.ts, som denne bygger videre på.
 *
 * Forholdet til `bySeller` i offer-pipeline-calc.ts: den gir ÉN sum per
 * selger. Denne gir radene bak summen, så DL kan ekspandere en rådgiver og se
 * hvilke tilbud som faktisk ligger der. Attribusjonsregelen er den samme
 * (`writers` med share_pct, fallback `created_by` 100 %), og er bevisst
 * gjenbrukt herfra i stedet for reimplementert, slik at widgeten aldri kan
 * vise en annen fordeling enn KPI-kortet.
 *
 * GEEK, ikke kontraktssum: William ba om «geek» i widgeten. `offers` har ingen
 * lagret GEEK-kolonne, så den regnes per tilbud med `calcOfferTotals().bf`
 * (src/lib/offer-calc.ts), som er den kanoniske kilden per
 * docs/kpi-source-of-truth.md → «GEEK estimert (tilbud)». Merk at dette skiller
 * seg fra `weightedPipeline`, som summerer `total_amount`: de to svarer på hvert
 * sitt spørsmål (hva kunden betaler mot hva vi sitter igjen med).
 */

import {
  isExpired,
  type OfferProbability,
  type PipelineOfferStatus,
  type ProbabilityWeights,
} from "./offer-pipeline-calc";

/** Ett tilbud slik widgeten trenger det, med GEEK allerede utregnet. */
export interface PipeOffer {
  id: string;
  title: string;
  customerName: string | null;
  status: PipelineOfferStatus;
  probability: OfferProbability | null;
  /** ISO-dato, prisens gyldighet. */
  valid_until: string | null;
  /** ISO-dato, rådgivers estimat på når kunden bestemmer seg. */
  expected_close_date: string | null;
  /** ISO-tidspunkt tilbudet ble sendt. */
  sent_at: string | null;
  /** GEEK for tilbudet, fra calcOfferTotals().bf. */
  bf: number;
  created_by: string;
  writers: Array<{ user_id: string; share_pct: number }>;
}

/** Ett tilbud slik det vises under en rådgiver, med andelen tilskrevet hen. */
export interface PipeOfferRow {
  id: string;
  title: string;
  customerName: string | null;
  probability: OfferProbability | null;
  expectedCloseDate: string | null;
  validUntil: string | null;
  sentAt: string | null;
  /** Rådgiverens andel av tilbudets GEEK. */
  bfKr: number;
  /** Andelen ganget med sannsynlighetsvekten. Null uten kategori. */
  weightedBfKr: number | null;
  /** Rådgiverens andel i prosent — vises kun når tilbudet er delt. */
  sharePct: number;
}

export interface AdvisorPipe {
  userId: string;
  /**
   * Σ bfKr for rådgiverens tilbud, avrundet til hele kroner.
   *
   * På et delt tilbud rundes hver rådgivers andel for seg, så summen over
   * rådgivere kan bomme med inntil én krone per rådgiver mot tilbudets fulle
   * GEEK. Resten fordeles bevisst ikke: en krone betyr ingenting i en
   * pipeline-widget, og rest-fordeling ville gjort at én rådgivers tall endret
   * seg avhengig av hvem andre som står på tilbudet.
   */
  bfKr: number;
  /** Σ weightedBfKr. Tilbud uten kategori bidrar ikke. */
  weightedBfKr: number;
  /** Tilbud uten sannsynlighet satt — de mangler i weightedBfKr. */
  uncategorizedCount: number;
  /** Tilbud uten estimert close-dato. */
  missingCloseDateCount: number;
  offers: PipeOfferRow[];
}

/**
 * Er tilbudet «i pipe»? Sendt og ikke utløpt — samme regel som KPI-kortet, så
 * widgeten og kortet aldri er uenige om hva som er ute.
 */
function isInPipe(offer: PipeOffer, today: Date): boolean {
  return offer.status === "sent" && !isExpired(offer, today);
}

/**
 * Fordeler et tilbud på rådgivere. `writers` med share_pct når satt, ellers
 * hele tilbudet på den som opprettet det. Identisk regel som `attributeOffer`
 * i offer-pipeline-calc.ts.
 */
function attribute(offer: PipeOffer): Array<{ userId: string; sharePct: number }> {
  if (offer.writers.length > 0) {
    return offer.writers.map((w) => ({ userId: w.user_id, sharePct: w.share_pct }));
  }
  return [{ userId: offer.created_by, sharePct: 100 }];
}

/**
 * Tilbud i pipe gruppert per rådgiver, sortert med størst vektet GEEK først.
 *
 * Tilbudene under hver rådgiver sorteres på estimert close-dato, tidligst
 * først, med de uten dato sist: det DL vil vite er hva som lander denne
 * måneden, og et tilbud uten dato er nettopp et som må følges opp.
 */
export function advisorPipes(
  offers: PipeOffer[],
  weights: ProbabilityWeights,
  today: Date,
): AdvisorPipe[] {
  const byUser = new Map<string, AdvisorPipe>();

  for (const offer of offers) {
    if (!isInPipe(offer, today)) continue;

    for (const { userId, sharePct } of attribute(offer)) {
      const fraction = sharePct / 100;
      const bfKr = offer.bf * fraction;
      const weighted =
        offer.probability != null ? bfKr * weights[offer.probability] : null;

      const entry =
        byUser.get(userId) ??
        {
          userId,
          bfKr: 0,
          weightedBfKr: 0,
          uncategorizedCount: 0,
          missingCloseDateCount: 0,
          offers: [],
        };

      entry.bfKr += bfKr;
      if (weighted != null) entry.weightedBfKr += weighted;
      else entry.uncategorizedCount += 1;
      if (!offer.expected_close_date) entry.missingCloseDateCount += 1;

      entry.offers.push({
        id: offer.id,
        title: offer.title,
        customerName: offer.customerName,
        probability: offer.probability,
        expectedCloseDate: offer.expected_close_date,
        validUntil: offer.valid_until,
        sentAt: offer.sent_at,
        bfKr: Math.round(bfKr),
        weightedBfKr: weighted == null ? null : Math.round(weighted),
        sharePct,
      });

      byUser.set(userId, entry);
    }
  }

  const result = [...byUser.values()].map((e) => ({
    ...e,
    bfKr: Math.round(e.bfKr),
    weightedBfKr: Math.round(e.weightedBfKr),
    offers: [...e.offers].sort(compareByCloseDate),
  }));

  return result.sort((a, b) => b.weightedBfKr - a.weightedBfKr);
}

/** Tidligste close-dato først, tilbud uten dato sist. */
function compareByCloseDate(a: PipeOfferRow, b: PipeOfferRow): number {
  if (a.expectedCloseDate && b.expectedCloseDate) {
    return a.expectedCloseDate.localeCompare(b.expectedCloseDate);
  }
  if (a.expectedCloseDate) return -1;
  if (b.expectedCloseDate) return 1;
  return a.title.localeCompare(b.title, "nb");
}

export interface PipeTotals {
  bfKr: number;
  weightedBfKr: number;
  offerCount: number;
  uncategorizedCount: number;
  missingCloseDateCount: number;
}

/**
 * Totalene over alle rådgivere.
 *
 * `offerCount` teller DISTINKTE tilbud, ikke rader: et tilbud delt mellom to
 * rådgivere er fortsatt ett tilbud i pipen, og å telle det to ganger ville
 * blåst opp tallet DL styrer etter. Kronene summeres derimot fra andelene, som
 * per definisjon går opp i tilbudets fulle GEEK.
 */
export function pipeTotals(pipes: AdvisorPipe[]): PipeTotals {
  const seen = new Set<string>();
  let uncategorized = 0;
  let missingCloseDate = 0;

  for (const pipe of pipes) {
    for (const o of pipe.offers) {
      if (seen.has(o.id)) continue;
      seen.add(o.id);
      if (o.probability == null) uncategorized += 1;
      if (!o.expectedCloseDate) missingCloseDate += 1;
    }
  }

  return {
    bfKr: pipes.reduce((s, p) => s + p.bfKr, 0),
    weightedBfKr: pipes.reduce((s, p) => s + p.weightedBfKr, 0),
    offerCount: seen.size,
    uncategorizedCount: uncategorized,
    missingCloseDateCount: missingCloseDate,
  };
}

/** Ett tilbud i pipen med full GEEK, samlet tilbake fra rådgivernes andeler. */
export interface PipeOfferTotal {
  id: string;
  title: string;
  customerName: string | null;
  sentAt: string | null;
  bfKr: number;
}

export interface PipeAgeBucket {
  label: string;
  offers: PipeOfferTotal[];
  bfKr: number;
}

/**
 * Hvert tilbud én gang, med andelene summert tilbake til full GEEK. Et delt
 * tilbud ligger under flere rådgivere i `pipes`.
 */
export function distinctPipeOffers(pipes: AdvisorPipe[]): PipeOfferTotal[] {
  const offers = new Map<string, PipeOfferTotal>();
  for (const pipe of pipes) {
    for (const o of pipe.offers) {
      const row = offers.get(o.id) ?? { id: o.id, title: o.title, customerName: o.customerName, sentAt: o.sentAt, bfKr: 0 };
      row.bfKr += o.bfKr;
      offers.set(o.id, row);
    }
  }
  return [...offers.values()];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Tilbudene fordelt på alder siden sendt (VIB-2990). Gamle tilbud er trolig
 * tapt eller glemt og blåser opp pipen. Tilbud uten sendt-dato havner i egen
 * bøtte, så summen av bøttene alltid er hele pipen.
 */
export function pipeAgeBuckets(offers: PipeOfferTotal[], today: Date): PipeAgeBucket[] {
  const buckets: PipeAgeBucket[] = [
    { label: "Siste 30 dager", offers: [], bfKr: 0 },
    { label: "31–90 dager", offers: [], bfKr: 0 },
    { label: "Over 90 dager", offers: [], bfKr: 0 },
    { label: "Uten sendt-dato", offers: [], bfKr: 0 },
  ];
  for (const o of offers) {
    const days = o.sentAt == null ? null : Math.floor((today.getTime() - new Date(o.sentAt).getTime()) / DAY_MS);
    const b = days == null ? buckets[3] : days <= 30 ? buckets[0] : days <= 90 ? buckets[1] : buckets[2];
    b.offers.push(o);
    b.bfKr += o.bfKr;
  }
  return buckets.filter((b) => b.offers.length > 0);
}

/** Kunder med flere tilbud ute samtidig, flest først. Ofte alternativer til hverandre. */
export function customersWithSeveralOffers(offers: PipeOfferTotal[]): Array<{ customer: string; offers: PipeOfferTotal[]; bfKr: number }> {
  const byCustomer = new Map<string, PipeOfferTotal[]>();
  for (const o of offers) {
    if (!o.customerName) continue;
    byCustomer.set(o.customerName, [...(byCustomer.get(o.customerName) ?? []), o]);
  }
  return [...byCustomer.entries()]
    .filter(([, list]) => list.length > 1)
    .map(([customer, list]) => ({ customer, offers: list, bfKr: list.reduce((s, o) => s + o.bfKr, 0) }))
    .sort((a, b) => b.offers.length - a.offers.length || b.bfKr - a.bfKr);
}
